import Button from '@rio-cloud/rio-uikit/Button';
import SmoothScrollbars from '@rio-cloud/rio-uikit/components/smoothScrollbars/SmoothScrollbars';
import Spinner from '@rio-cloud/rio-uikit/components/spinner/Spinner';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import MarkdownRenderers from '@/components/molecule/MarkdownRenderers/MarkdownRenderers';
import {AnswerWrapper, ShowAnswer} from "@/components/organism/CommentDialog/CommentDialog.styles";
import { COMMENT_DIALOG_ACTIONS,COMMENT_DIALOG_CATEGORY, gaPush } from '@/configuration/setup/googleAnalytics';
import { TDataVisualization } from '@/services/threadApi/types';

type CommentDialogProps = {
  showDialog: boolean;
  handleClose: () => void;
  question: string | null;
  answer: string;
  isLoading: boolean;
  updateDetails: (details: string) => void;
  data_visualization?: TDataVisualization | null;
  messageId: string;
};

const texts = {
  title: 'common.feedback.feedbackRequest.title',
  subtitle: 'common.feedback.feedbackRequest.appeal',
  howToImproveInquiry: 'chatPage.commentDialog.feedback.howImproveAnswer.inquiry',
  howToImproveAppeal: 'chatPage.commentDialog.feedback.howImproveAnswer.appeal',
  commentPlaceholder: 'common.feedback.comment.placeholder',
  questionLabel: 'chatPage.commentDialog.feedback.question.label',
  answerLabel: 'chatPage.commentDialog.feedback.answer.label',
  showMore: 'common.showMore.toggle',
  showLess: 'common.showLess.toggle',
  submit: 'common.feedback.submit.label',
  validationInvalidCharactersError: 'common.feedback.validation.invalidCharacters.error',
  validationEmptyError: 'common.feedback.validation.empty.error',
  validationTooLongError: 'common.feedback.validation.tooLong.error',
};

const classes = {
  toolbar: 'btn-toolbar margin-bottom-20',
  spinnerWrapper: 'display-flex justify-content-center align-items-center',
  textMedium: 'text-medium text-size-18 margin-bottom-0',
  formGroup: 'form-group',
  questionLabel: 'text-right line-height-20 padding-right-5 text-medium text-size-16',
  answerLabel: 'text-right line-height-20 padding-right-5 text-medium text-size-16',
  flexColumn: 'display-flex flex-column justify-content-start align-items-start',
};

export const CommentDialog = ({
  showDialog,
  isLoading,
  handleClose,
  question,
  answer,
  updateDetails,
  data_visualization,
  messageId,
}: CommentDialogProps) => {
  const [enableSubmit, setEnableSubmit] = useState(false);
  const [comment, setComment] = useState<string>('');
  const [answerVisibility, setAnswerVisibility] = useState(false);
  const intl = useIntl();

  const validateInput = (value: string): string | boolean => {
    const specialCharacterRegex = /^[^{}<>]*$/;

    if (!specialCharacterRegex.test(value)) {
      return intl.formatMessage({ id: texts.validationInvalidCharactersError });
    }

    if (value.trim().length === 0) {
      return intl.formatMessage({ id: texts.validationEmptyError });
    }

    if (value.length > 300) {
      return intl.formatMessage({ id: texts.validationTooLongError });
    }

    return true;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validationResult = validateInput(comment);

    if (validationResult === true) {
      updateDetails(comment);

      gaPush({
        category: COMMENT_DIALOG_CATEGORY,
        action: COMMENT_DIALOG_ACTIONS.SUBMIT_COMMENT,
        label: `Comment Length: ${comment.length}`,
      });
    } else {
      console.log(validationResult);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.currentTarget.value;
    setComment(value);
    const isValid = validateInput(value) === true;
    setEnableSubmit(isValid && value.trim().length > 0);
  };

  const handleShowMore = () => {
    setAnswerVisibility(true);
    gaPush({
      category: COMMENT_DIALOG_CATEGORY,
      action: COMMENT_DIALOG_ACTIONS.SHOW_MORE_ANSWER,
      label: `Answer Length: ${answer.length}`,
    });
  };

  const handleShowLess = () => {
    setAnswerVisibility(false);
    gaPush({
      category: COMMENT_DIALOG_CATEGORY,
      action: COMMENT_DIALOG_ACTIONS.SHOW_LESS_ANSWER,
      label: `Answer Length: ${answer.length}`,
    });
  };

  return (
    <div className={classes.toolbar}>
      <Dialog show={showDialog} onClose={handleClose} showCloseButton bsSize="md">
        {isLoading ? (
          <Dialog.Body>
            <div className={classes.spinnerWrapper} style={{ minHeight: '300px' }}>
              <Spinner data-testid="spinner" />
            </div>
          </Dialog.Body>
        ) : (
          <form data-testid="feedback-comment-modal" onSubmit={handleSubmit}>
            <Dialog.Title
              title={<FormattedMessage id={texts.title} />}
              subtitle={<FormattedMessage id={texts.subtitle} />}
            />
            <SmoothScrollbars className="height-50vh">
              <Dialog.Body>
                <div>
                  <div className={classes.formGroup}>
                    <p className={classes.textMedium}>
                      <FormattedMessage id={texts.howToImproveInquiry} />
                    </p>
                    <label htmlFor="commentInput">
                      <FormattedMessage id={texts.howToImproveAppeal} />
                    </label>
                    <textarea
                      className="form-control"
                      name="commentInput"
                      placeholder={intl.formatMessage({ id: texts.commentPlaceholder })}
                      onChange={handleInputChange}
                      value={comment}
                      rows={4}
                      data-testid="feedback-comment-input"
                    />
                  </div>
                  <div className={classes.formGroup}>
                    <div className={classes.flexColumn}>
                      <label className={classes.questionLabel}>
                        <FormattedMessage id={texts.questionLabel} />:
                      </label>
                      <div>{question}</div>
                    </div>
                  </div>
                  <div className={classes.formGroup}>
                    <div className={classes.flexColumn}>
                      <label className={classes.answerLabel}>
                        <FormattedMessage id={texts.answerLabel} />:
                      </label>
                      <AnswerWrapper data-testid={answerVisibility ? 'feedback-full-answer' : ''} expanded={answerVisibility}>
                        <ReactMarkdown components={MarkdownRenderers({message: answer, showActions: false, data_visualization, messageId})} remarkPlugins={[remarkGfm]}>
                          {answer}
                        </ReactMarkdown>
                      </AnswerWrapper>
                      {answer.length > 400 && (
                        <>
                          {!answerVisibility && (
                            <ShowAnswer data-testid="feedback-show-more-button" onClick={handleShowMore}>
                              <FormattedMessage id={texts.showMore} />
                            </ShowAnswer>
                          )}
                          {answerVisibility && (
                            <ShowAnswer data-testid="feedback-show-less-button" onClick={handleShowLess}>
                              <FormattedMessage id={texts.showLess} />
                            </ShowAnswer>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Dialog.Body>
            </SmoothScrollbars>
            <Dialog.Footer>
              <div>
                <Button data-testid="feedback-submit-button" bsStyle="primary" type="submit" disabled={!enableSubmit}>
                  <FormattedMessage id={texts.submit} />
                </Button>
              </div>
            </Dialog.Footer>
          </form>
        )}
      </Dialog>
    </div>
  );
};
